import { serverCopyActivity, serverMoveActivityWithinCourse } from "api/api-server-actions";
import {
  ActivityCategory,
  ActivityType,
  LessonOrArchiveActivityCategory,
} from "components/server-types";
import { t } from "i18n/i18n";
import { storeApi } from "store/index";
import { ActivityWithPagesType } from "store/selectors";
import {
  toastLocalizedOperationCatcher,
  toastMoveOperationCatcher,
  toastSuccessMessage,
} from "utils/alerts";

export type ReleaseStatus = "released" | "unreleased" | "partiallyReleased" | null;

export const moveActivityWithinCourse = (itemId: string, destinationTopicId: string) => {
  const destinationKey = "topic";
  serverMoveActivityWithinCourse(itemId, destinationTopicId)
    .then(() => {
      toastSuccessMessage(
        t("content_view.item_moved", { destinationCategory: t(`glossary.${destinationKey}`) }),
      );
    })
    .catch(toastMoveOperationCatcher(destinationKey));
};

export const copyActivity = (activityId: string) => {
  serverCopyActivity(activityId).catch(
    toastLocalizedOperationCatcher("failed_to_duplicate_activity"),
  );
};

export const isArchived = (category: ActivityCategory) => category === ActivityCategory.ARCHIVE;

export const isLesson = (category: ActivityCategory) => category === ActivityCategory.LESSON;

export const isPrep = (category: ActivityCategory) => category === ActivityCategory.PREP;

export const isAdditionalResource = (category: ActivityCategory) =>
  category === ActivityCategory.ADDITIONAL;

/*
 * The following hook returns functions for maintaining activities in the store.
 */
export const useActivityActions = () => {
  const createActivity = (category: ActivityCategory, topicId: string, courseId: string) => {
    if (topicId === null) {
      // TODO: raise an error?
      return;
    }

    return storeApi.activities.create({
      title: t("glossary.activity"),
      topic_id: topicId,
      course_id: courseId,
      category,
      estimated_time: undefined,
    });
  };

  const saveActivity = (activity: Partial<ActivityWithPagesType> & { id: string }) =>
    storeApi.activities.partial_update(activity);

  // Temporary confirm dialog until we get the final design. Returns true if the user confirms wanting to archive.
  const confirmArchiveActivity = () => {
    return window.confirm(
      "Are you sure you want to move this activity to the archive? It will become unavailable to students, even if already released.",
    );
  };

  const unpublishAllPagesForActivity = (activity: ActivityWithPagesType) =>
    Promise.all(
      activity.pages.map((page) =>
        // TODO: We currently don't have a way of doing this in bulk. Also, it might be better
        // to handle this on the backend.
        storeApi.pages.partial_update({
          id: page.id,
          activity_id: activity.id,
          released_at: null,
        }),
      ),
    );

  // Changes the activity type between `lesson` or `archive`, presenting alerts if necessary. Not for use
  // with drag and drop, which modifies the activity locally during the drag operation.
  const onActivityTypeChanged = async (
    activity: ActivityWithPagesType,
    newCategory: LessonOrArchiveActivityCategory,
  ) => {
    if (activity.category === newCategory) {
      return false;
    }

    // Pages are unpublished on archive. We confirm with the user before unpublishing.
    if (isArchived(newCategory) && activity.pages.some((page) => page.released_at)) {
      if (!confirmArchiveActivity()) {
        return;
      }
      await unpublishAllPagesForActivity(activity);
    }

    return saveActivity({ id: activity.id, category: newCategory });
  };

  const deleteActivity = (activity: ActivityType) => storeApi.activities.destroy(activity.id);

  const archiveActivity = (activity: ActivityWithPagesType) =>
    onActivityTypeChanged(activity, ActivityCategory.ARCHIVE);

  const unarchiveActivity = (activity: ActivityWithPagesType) =>
    onActivityTypeChanged(activity, ActivityCategory.LESSON);

  const dropActivity = async (activity: ActivityWithPagesType) => {
    // Pages are unpublished on archive. We confirm with the user before unpublishing.
    if (isArchived(activity.category) && activity.pages.some((r) => r.released_at)) {
      const confirm = confirmArchiveActivity();
      if (!confirm) {
        return;
      }

      await unpublishAllPagesForActivity(activity);
    }

    return saveActivity({ id: activity.id, category: activity.category, order: activity.order });
  };

  return {
    createActivity,
    saveActivity,
    deleteActivity,
    dropActivity,
    archiveActivity,
    unarchiveActivity,
  };
};
